import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const InvoiceStatusSchema = z.enum(["DRAFT", "COMMITTED", "VOID"]);

// Derived payment-state shown to operators. Computed server-side so the list
// and detail views always agree. Independent from the KB lifecycle status above.
export const InvoiceDisplayStatusSchema = z.enum([
  "PAID",
  "CREDITED", // balance settled by account credit / CBA — no money moved
  "CREDIT_ISSUED", // KB-internal invoice that ADDED account credit (origin trail)
  "UNPAID",
  "OVERDUE",
  "PROCESSING", // a payment is in flight (gateway authorize/capture pending)
  "FAILED", // last payment attempt failed and balance is still owed
  "REFUNDED",
  "VOID",
  "DRAFT",
]);

const InvoiceLineItemSchema = z.object({
  invoiceItemId: z.string(),
  itemType: z.string(),
  description: z.string().nullable(),
  planName: z.string().nullable(),
  phase: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
});

const PaymentTransactionSchema = z.object({
  transactionId: z.string(),
  transactionType: z.string(),
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  status: z.string(),
  gatewayErrorCode: z.string().nullable(),
  gatewayErrorMsg: z.string().nullable(),
  paymentId: z.string().nullable(),
  paymentMethodType: z.enum(["CARD", "EXTERNAL", "OTHER"]).default("OTHER"),
  paymentMethodLabel: z.string().default(""),
  bankReference: z.string().nullable(),
  bankAccountId: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankAccountLabel: z.string().nullable(),
  receivedDate: z.string().nullable(),
  notes: z.string().nullable(),
});

const InvoiceSummarySchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.number().nullable(),
  invoiceDate: z.string().nullable(),
  currency: z.string(),
  status: InvoiceStatusSchema,
  displayStatus: InvoiceDisplayStatusSchema,
  balance: z.number(),
  amount: z.number().nullable(),
  // Distinct bundleIds present on this invoice's RECURRING/CHARGE items.
  // Used by the subscription list to flag which bundles have unpaid invoices.
  bundleIds: z.array(z.string()),
});

const InvoiceDetailSchema = InvoiceSummarySchema.extend({
  items: z.array(InvoiceLineItemSchema),
  payments: z.array(PaymentTransactionSchema),
  dueDate: z.string().nullable(),
});

const RecordExternalPaymentInputSchema = z.object({
  amount: z.number().positive(),
  currency: z.string(),
  receivedDate: z.string().optional(),
  bankReference: z.string().optional(),
  bankAccountId: z.string().optional(),
  notes: z.string().optional(),
});

const AuthorizeCardInputSchema = z.object({
  amount: z.number().positive(),
  currency: z.string(),
});

const CapturePaymentInputSchema = z.object({
  amount: z.number().positive(),
  currency: z.string(),
});

const RefundInputSchema = z.object({
  amount: z.number().positive(),
  currency: z.string(),
});

// `currency` is derived server-side from the franchise's billing account —
// that account is the only authority on which currency a credit can be in.
// Supplying it is optional and acts purely as an assertion: a mismatch is
// rejected with 400 rather than silently forwarded to Kill Bill.
const IssueCreditInputSchema = z.object({
  amount: z.number().positive(),
  currency: z.string().optional(),
  description: z.string().optional(),
});

const IssueCreditResponseSchema = z.object({
  creditId: z.string(),
  amount: z.number(),
  currency: z.string(),
  description: z.string().nullable(),
});

// Currency is resolved per franchise from its own billing account, so a batch
// spanning several currencies issues `amount` in each account's currency —
// no FX conversion is applied. See IssueCreditInputSchema on the optional
// `currency` assertion.
const BulkCreditInputSchema = z.object({
  franchiseIds: z.array(z.string()).min(1),
  amount: z.number().positive(),
  currency: z.string().optional(),
  description: z.string().optional(),
});

const BulkCreditResponseSchema = z.object({
  totalRequested: z.number(),
  successCount: z.number(),
  failureCount: z.number(),
  results: z.array(
    z.object({
      franchiseId: z.string(),
      status: z.enum(["success", "failed"]),
      creditId: z.string().optional(),
      error: z.string().optional(),
    })
  ),
});

const ArAgingBucketSchema = z.object({
  label: z.string(),
  minDays: z.number(),
  maxDays: z.number().nullable(),
  total: z.number(),
  count: z.number(),
});

const ArAgingRowSchema = z.object({
  franchiseId: z.string(),
  franchiseName: z.string().nullable(),
  kbAccountId: z.string(),
  currency: z.string(),
  outstandingTotal: z.number(),
  oldestInvoiceDate: z.string().nullable(),
  oldestInvoiceDays: z.number().nullable(),
  invoiceCount: z.number(),
});

const ArAgingResponseSchema = z.object({
  asOf: z.string(),
  totalOutstanding: z.number(),
  currency: z.string().nullable(),
  accountsChecked: z.number(),
  buckets: z.array(ArAgingBucketSchema),
  rows: z.array(ArAgingRowSchema),
});

const InvoiceDraftLineSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  amount: z.number().positive(),
  currency: z.string(),
  description: z.string(),
  bundleId: z.string().nullable(),
  subscriptionId: z.string().nullable(),
});

export const InvoiceDraftSchema = z.object({
  draftId: z.string(),
  franchiseId: z.string(),
  sourceInvoiceId: z.string().nullable(),
  currency: z.string(),
  voidedSourceAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  lines: z.array(InvoiceDraftLineSchema),
});

const DraftLineInputSchema = z.object({
  amount: z.number().positive(),
  description: z.string().min(1),
});

const errorSchema = z.object({ message: z.string() });

export const invoiceContract = c.router({
  listInvoices: {
    method: "GET",
    path: "/billing/invoices/:franchiseId",
    pathParams: z.object({ franchiseId: z.string() }),
    query: z.object({
      page: z.coerce.number().int().positive().default(1),
      limit: z.coerce.number().int().positive().max(100).default(20),
      bundleId: z.string().optional(),
    }),
    responses: {
      200: z.array(InvoiceSummarySchema),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "List invoices for a franchise (ordered by date desc)",
  },

  getInvoice: {
    method: "GET",
    path: "/billing/invoices/:franchiseId/:invoiceId",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    responses: {
      200: InvoiceDetailSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get invoice detail with line items and payment transactions",
  },

  recordExternalPayment: {
    method: "POST",
    path: "/billing/invoices/:franchiseId/:invoiceId/payments/external",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    body: RecordExternalPaymentInputSchema,
    responses: {
      201: PaymentTransactionSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Record an external (bank transfer) payment against an invoice",
  },

  updatePaymentBankAccount: {
    method: "PATCH",
    path: "/billing/invoices/:franchiseId/payments/:paymentId/bank-account",
    pathParams: z.object({ franchiseId: z.string(), paymentId: z.string() }),
    body: z.object({
      /** Pass null to clear the association. */
      bankAccountId: z.string().nullable(),
    }),
    responses: {
      200: z.object({
        paymentId: z.string(),
        bankAccountId: z.string().nullable(),
        bankAccountNumber: z.string().nullable(),
        bankAccountLabel: z.string().nullable(),
      }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update the bank account associated with a recorded external payment (metadata only)",
  },

  authorizeCard: {
    method: "POST",
    path: "/billing/payments/:paymentId/authorize",
    pathParams: z.object({ paymentId: z.string() }),
    body: AuthorizeCardInputSchema,
    responses: {
      201: PaymentTransactionSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Issue an authorization-only card payment",
  },

  capturePayment: {
    method: "POST",
    path: "/billing/payments/:paymentId/capture",
    pathParams: z.object({ paymentId: z.string() }),
    body: CapturePaymentInputSchema,
    responses: {
      201: PaymentTransactionSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Capture a previously authorized payment",
  },

  voidPayment: {
    method: "POST",
    path: "/billing/payments/:paymentId/void",
    pathParams: z.object({ paymentId: z.string() }),
    body: z.object({}),
    responses: {
      201: PaymentTransactionSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Void an outstanding authorization",
  },

  issueCredit: {
    method: "POST",
    path: "/billing/franchises/:franchiseId/credits",
    pathParams: z.object({ franchiseId: z.string() }),
    body: IssueCreditInputSchema,
    responses: {
      201: IssueCreditResponseSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Issue an account credit to a franchise (goodwill / dispute resolution)",
  },

  writeOffCredit: {
    method: "POST",
    path: "/billing/franchises/:franchiseId/credits/write-off",
    pathParams: z.object({ franchiseId: z.string() }),
    body: IssueCreditInputSchema,
    responses: {
      201: z.object({
        invoiceId: z.string(),
        amount: z.number(),
        currency: z.string(),
        description: z.string(),
      }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Consume account credit by issuing a one-off charge that nets to zero",
  },

  getInvoiceHtml: {
    method: "GET",
    path: "/billing/invoices/:franchiseId/:invoiceId/html",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    responses: {
      200: z.object({ html: z.string() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Render an invoice as HTML (used by the print-to-PDF flow)",
  },

  getArAging: {
    method: "GET",
    path: "/billing/reports/ar-aging",
    responses: {
      200: ArAgingResponseSchema,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Accounts-receivable aging report across all franchises",
  },

  bulkIssueCredit: {
    method: "POST",
    path: "/billing/credits/bulk",
    body: BulkCreditInputSchema,
    responses: {
      201: BulkCreditResponseSchema,
      400: errorSchema,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Issue the same credit to multiple franchises in one call",
  },

  refundPayment: {
    method: "POST",
    path: "/billing/payments/:paymentId/refund",
    pathParams: z.object({ paymentId: z.string() }),
    body: RefundInputSchema,
    responses: {
      201: PaymentTransactionSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Refund a payment (full or partial)",
  },

  emailInvoice: {
    method: "POST",
    path: "/billing/invoices/:franchiseId/:invoiceId/email",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    body: z.object({ to: z.string().email().optional() }),
    responses: {
      200: z.object({ to: z.string(), messageId: z.string().nullable(), success: z.boolean() }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Send an invoice by email to the franchise billing address (or a custom address)",
  },

  adjustInvoiceItem: {
    method: "POST",
    path: "/billing/invoices/:franchiseId/:invoiceId/items/:invoiceItemId/adjust",
    pathParams: z.object({
      franchiseId: z.string(),
      invoiceId: z.string(),
      invoiceItemId: z.string(),
    }),
    body: z.object({
      amount: z.number().positive(),
      description: z.string().optional(),
    }),
    responses: {
      200: z.object({
        invoiceItemId: z.string(),
        amount: z.number(),
        currency: z.string(),
        description: z.string().nullable(),
      }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Apply a credit-style adjustment to a specific invoice line item (reduces the charge)",
  },

  applyInvoiceDiscount: {
    method: "POST",
    path: "/billing/invoices/:franchiseId/:invoiceId/discount",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    body: z.object({
      amount: z.number().positive(),
      description: z.string().optional(),
    }),
    responses: {
      201: z.object({
        invoiceItemId: z.string(),
        amount: z.number(),
        currency: z.string(),
        description: z.string().nullable(),
      }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary:
      "Apply an invoice-level discount (CREDIT_ADJ) — quick way to reduce balance on a specific invoice",
  },

  voidInvoice: {
    method: "POST",
    path: "/billing/invoices/:franchiseId/:invoiceId/void",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    body: z.object({ reason: z.string().optional() }),
    responses: {
      200: z.object({ invoiceId: z.string(), status: z.literal("VOID") }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Void an invoice (only allowed for unpaid COMMITTED invoices)",
  },

  // Drafts live entirely in salon-backend's database — no KB DRAFT invoices are
  // ever created. KB only sees the final committed invoice (and the void of the
  // source it replaces). See openspec/changes/local-draft-invoices.

  recreateInvoiceAsDraft: {
    method: "POST",
    path: "/billing/invoices/:franchiseId/:invoiceId/recreate-as-draft",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    body: z.object({ reason: z.string().optional() }),
    responses: {
      201: InvoiceDraftSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary:
      "Create a local editable draft populated from an existing COMMITTED unpaid invoice. Source invoice is NOT voided until the draft is committed.",
  },

  listInvoiceDrafts: {
    method: "GET",
    path: "/billing/invoice-drafts/:franchiseId",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: z.array(InvoiceDraftSchema),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "List local invoice drafts for a franchise",
  },

  getInvoiceDraft: {
    method: "GET",
    path: "/billing/invoice-drafts/:franchiseId/:draftId",
    pathParams: z.object({ franchiseId: z.string(), draftId: z.string() }),
    responses: {
      200: InvoiceDraftSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get a single invoice draft by id",
  },

  addInvoiceDraftLine: {
    method: "POST",
    path: "/billing/invoice-drafts/:franchiseId/:draftId/lines",
    pathParams: z.object({ franchiseId: z.string(), draftId: z.string() }),
    body: DraftLineInputSchema,
    responses: {
      201: InvoiceDraftSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Append a line to an invoice draft",
  },

  updateInvoiceDraftLine: {
    method: "PUT",
    path: "/billing/invoice-drafts/:franchiseId/:draftId/lines/:lineId",
    pathParams: z.object({ franchiseId: z.string(), draftId: z.string(), lineId: z.string() }),
    body: DraftLineInputSchema,
    responses: {
      200: InvoiceDraftSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update a draft line (amount and/or description)",
  },

  removeInvoiceDraftLine: {
    method: "DELETE",
    path: "/billing/invoice-drafts/:franchiseId/:draftId/lines/:lineId",
    pathParams: z.object({ franchiseId: z.string(), draftId: z.string(), lineId: z.string() }),
    body: z.object({}),
    responses: {
      200: InvoiceDraftSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Remove a line from an invoice draft",
  },

  commitInvoiceDraft: {
    method: "POST",
    path: "/billing/invoice-drafts/:franchiseId/:draftId/commit",
    pathParams: z.object({ franchiseId: z.string(), draftId: z.string() }),
    body: z.object({}),
    responses: {
      201: InvoiceDetailSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary:
      "Commit a draft: void the source invoice (if any) and create a new COMMITTED invoice in Kill Bill with the draft lines",
  },

  discardInvoiceDraft: {
    method: "POST",
    path: "/billing/invoice-drafts/:franchiseId/:draftId/discard",
    pathParams: z.object({ franchiseId: z.string(), draftId: z.string() }),
    body: z.object({}),
    responses: {
      200: z.object({ draftId: z.string(), status: z.literal("DISCARDED") }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Discard a local draft. Source invoice is left untouched if it was not yet voided.",
  },

  setInvoiceDueDate: {
    method: "PUT",
    path: "/billing/invoices/:franchiseId/:invoiceId/due-date",
    pathParams: z.object({ franchiseId: z.string(), invoiceId: z.string() }),
    body: z.object({ dueDate: z.string().nullable() }),
    responses: {
      200: z.object({ dueDate: z.string().nullable() }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Set or clear a due date on an invoice (stored as a KB custom field)",
  },
});
